import React from "react";
import GenericCard from "../shared/GenericCard";
import { PanelLoadContext } from "../shared/PanelShell";
import { labelFor } from "../shared/format";
import KeyFindings from "./KeyFindings";
import TheDrain from "./TheDrain";
import BarrierGroups from "./BarrierGroups";
import Workload from "./Workload";
import CallShape from "./CallShape";
import MoneyStrip from "./panels/MoneyStrip";
import TrendChart from "./panels/TrendChart";
import GreetingLossPanel from "./panels/GreetingLossPanel";
import TimeSavedPanel from "./panels/TimeSavedPanel";
import FollowupsPanel from "./panels/FollowupsPanel";
import InProgressPanel from "./panels/InProgressPanel";
import BarsPanel from "./panels/BarsPanel";
import AttributionPanel from "./panels/AttributionPanel";
import WebsiteGapsPanel from "./panels/WebsiteGapsPanel";
import ChatQuestionsPanel from "./panels/ChatQuestionsPanel";

// ANALYSIS COLUMN — the right column of the ConversationPage, composed in the
// Part-B tier order (V6 §B.2). Render-only: every number and sentence is already
// fetched (loadConversationData) and passed in. This is the composition site the v1
// tier containers used to be — the tiers are deleted and their panel wiring lives
// here, flat.
//
// TIER ORDER IS FIXED (§B.2) and the WIDTHS are §B.3: the money strip's four cells
// (Addendum 1 §2, 1.55/1/1/1), the Barriers+Guest-questions pair (1.4/1 Phone, 1/1.4
// Chat), Demand full width, then the Follow-ups+In-progress pair (1/1, Addendum 1 §3).
// The order also keeps the form-variety rule (§3.3, "no two adjacent panels share a
// form"): on Phone, Greeting loss (bars) and Demand (bars) are separated by
// Barriers/Guest-questions, so no two bar charts sit adjacent; the two adjacent
// worklists (Follow-ups, In-progress) read as different forms — prose reasons vs a
// reservation ledger with amounts.
//
// PANEL CHOICE (user ruling 2026-09-01): the existing conversation-specific v1 panels
// are wired here, NOT the welded weather/search-console catalogue forms the plan
// named — those carry their origin feature's data shape and labels. CallShape and
// Workload are thin new conversation panels (their catalogue equivalents were welded
// or would repeat a form).
//
// UNKNOWN panel_key → GenericCard (§G.4): a sentence key this release does not
// recognise renders as its title + sentence rather than being dropped or crashing.
//
// DEFERRED to fast-follow (V6 §G.2): the four confidence renderings (sample-size
// shown at medium, de-emphasis at low, "Not enough … yet to say" at insufficient)
// are NOT built into these panels this release — a `confidence` field does not yet
// exist, and the reference client is all above the ten-person floor. The one
// confidence rule that IS enforced now is "below ten people show a raw count, never
// a percentage" (ChatQuestionsPanel). Wire the full set before a low-volume client.

const KNOWN_KEYS = {
  voice: [
    "voice_verdict", "voice_funnel", "voice_greeting_loss", "voice_barriers",
    "voice_recovery", "voice_website_gaps", "voice_after_hours", "voice_time_saved",
    "voice_demand", "voice_bookings", "voice_attribution", "voice_followups",
    "voice_trend", "voice_workload", "voice_call_shape",
  ],
  chat: [
    "chat_verdict", "chat_bookings", "chat_recovery", "chat_website_gaps",
    "chat_questions", "chat_followups", "chat_demand", "chat_barriers",
    "chat_deflection", "chat_attribution", "chat_trend", "chat_workload", "chat_funnel",
  ],
};

// Which panels each load-time payload feeds (base keys; the page prefixes the
// modality). When a payload's request fails, these panels show "Couldn't load" with
// a retry instead of "No data for this period".
const PANELS_BY_SOURCE = {
  funnel: ["funnel"],
  summary: ["barriers", "demand", "attribution"],
  agentValue: ["time_saved", "workload"],
  trend: ["trend"],
  followups: ["followups"],
  callLoss: ["greeting_loss", "call_shape"],
  topics: ["questions"],
};

/**
 * The panelKeys that lost their data to a failed load-time request.
 * @param {string[]|undefined} failed the loader's `failed` source keys
 * @param {'voice'|'chat'} prefix
 * @returns {Set<string>}
 */
export const failedPanelKeys = (failed, prefix) =>
  new Set((failed || []).flatMap((src) => (PANELS_BY_SOURCE[src] || []).map((k) => `${prefix}_${k}`)));

// Memoised: opening the drill drawer (a ConversationPage state change) should not
// re-render every chart and panel here. All props from ConversationPage are stable
// refs (memoised callbacks, `say`/`headerAction` via useMemo, route-level range/
// clientId/modality), so this skips those unrelated re-renders.
const AnalysisColumn = React.memo(({
  modality = "voice",
  data,
  say = {},
  clientId,
  range,
  loading = false,
  activeStage = null,
  onFinding,
  onStageClick,
  onBarrierDrill,
  onDemandSelect,
  onFollowupSelect,
  onGapSelect,
  onGapFilter,
  onTopicSelect,
  onWorkloadDrill,
  onMoneyDrill,
  onAttributionSelect,
  onRetry = null,
  headerAction = null,
}) => {
  const p = modality === "chat" ? "chat" : "voice";
  const isVoice = p === "voice";
  const known = KNOWN_KEYS[p] || [];
  const unknownKeys = Object.keys(say).filter((k) => !known.includes(k));

  const failed = data?.failed;
  const loadStatus = React.useMemo(
    () => ({ failedPanels: failedPanelKeys(failed, p), onRetry }),
    [failed, p, onRetry],
  );

  const demand = data?.summary?.demand || {};
  const start = range?.start;
  const end = range?.end;

  return (
    <PanelLoadContext.Provider value={loadStatus}>
    <div data-testid="analysis-column" className="space-y-3">
      {/* Page title — bold h1 + descriptive subtitle at the top of the analysis
          (the header treatment the retired *AnalysisBand carried). Phone/Chat per
          the V6 rename; same classes as the other dashboards' headers. */}
      <header data-testid="analysis-title" className="flex items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {isVoice ? "Phone" : "Chat"}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {isVoice
              ? "How your phone agent handles calls, and where bookings are won or lost."
              : "How your chat agent handles conversations, and where bookings are won or lost."}
          </p>
        </div>
        {/* Optional right-aligned action, inline with the title (e.g. the
            "Show conversations" toggle). */}
        {headerAction}
      </header>
      {/* Panels keep the roomier 24px rhythm among themselves; only the gap between
          the header and the first panel is tightened (root space-y-3), so the page
          reads denser at the top without crowding the panels. */}
      <div className="space-y-6">
      {/* Tier 1 — verdict (first, largest) + Key Findings. */}
      <KeyFindings
        panelKey={`${p}_verdict`}
        say={say}
        funnel={data?.funnel}
        agentValue={data?.agentValue}
        onFinding={onFinding}
        loading={loading}
      />

      {/* Tier 2 — money strip + trend. */}
      {/* The money-strip figures are display-only — no drill into records (the
          onDrill handler is intentionally not passed, so each cell renders as a plain
          number rather than a button). */}
      <MoneyStrip
        say={say}
        panelKeyPrefix={p}
        bookings={data?.bookings}
        agentValue={data?.agentValue}
        showAfterHours={isVoice}
        loading={loading}
      />
      <TrendChart panelKey={`${p}_trend`} say={say} trend={data?.trend} loading={loading} />

      {/* Tier 3 — the order differs by modality (Markup #11).
          PHONE: the drain leads, paired 50/50 with Barriers (user ruling 2026-09-04),
          then greeting loss (Phone-only), then Guest questions.
          CHAT: the spec puts Guest questions BEFORE Barriers, so the drain leads full
          width, then Guest questions, then Barriers — the drain/barriers pair is not
          used on Chat. The drain is still first in the DOM, so it leads its tier on
          both. */}
      {isVoice ? (
        <>
          <div className="grid grid-cols-1 min-[1900px]:grid-cols-2 gap-4">
            <TheDrain
              panelKey="voice_funnel"
              say={say}
              stages={data?.funnel?.stages}
              activeStage={activeStage}
              onStageClick={onStageClick}
              loading={loading}
              fullWidth={false}
            />
            <BarrierGroups
              panelKey="voice_barriers"
              say={say}
              barriers={demand.top_barriers}
              loading={loading}
            />
          </div>
          {/* Greeting loss is Phone-only (no greeting to abandon on chat), full width. */}
          <GreetingLossPanel panelKey="voice_greeting_loss" say={say} callLoss={data?.callLoss} loading={loading} />
          <WebsiteGapsPanel
            panelKey="voice_website_gaps"
            label="In guests' own words"
            say={say}
            clientId={clientId}
            modality="voice"
            onFilter={onGapFilter}
            onSelect={onGapSelect}
          />
        </>
      ) : (
        <>
          <TheDrain
            panelKey="chat_funnel"
            say={say}
            stages={data?.funnel?.stages}
            activeStage={activeStage}
            onStageClick={onStageClick}
            loading={loading}
          />
          <WebsiteGapsPanel
            panelKey="chat_website_gaps"
            label="In guests' own words"
            say={say}
            clientId={clientId}
            modality="chat"
            onFilter={onGapFilter}
            onSelect={onGapSelect}
          />
          <BarrierGroups
            panelKey="chat_barriers"
            say={say}
            barriers={demand.top_barriers}
            loading={loading}
          />
        </>
      )}
      {/* Demand — its own row now, full width. Follow-ups pairs with In-progress
          below (both are worklists of people to contact), so Demand moves up out
          of the pair rather than sharing a row with a single worklist. */}
      <BarsPanel
        panelKey={`${p}_demand`}
        label="What people wanted"
        say={say}
        rows={demand.by_stay_type}
        loading={loading}
        ordered
      />
      {/* Follow-ups + In-progress pair (1/1) — Addendum 1 §3. Both are the same
          kind of work: people to contact today. In-progress self-fetches its
          reservation rows (fn_drill), so it needs only client / range / modality. */}
      <div className="grid grid-cols-1 min-[1900px]:grid-cols-2 gap-4">
        <FollowupsPanel
          panelKey={`${p}_followups`}
          say={say}
          followups={data?.followups}
          loading={loading}
          modality={p}
          onSelect={onFollowupSelect}
        />
        <InProgressPanel
          panelKey={`${p}_in_progress`}
          clientId={clientId}
          range={range}
          modality={p}
          // The authoritative count/value from fn_get_conversation_bookings (the same
          // source the money-strip "In progress" cell uses), so the card's headline
          // number matches the strip (Chat/Phone Markup #11). The rows below still come
          // from fn_drill; a row/aggregate gap is a backend reconciliation, flagged.
          inProgress={data?.bookings?.in_progress}
        />
      </div>

      {/* Tier 4 — quieter depth, two-up (user ruling 2026-09-04: 50% width, two
          rows). Stays full-width (100%) below 1900px and only splits to 2 columns at
          ≥1900px (user ruling 2026-09-05): with the 340px rail the analysis column
          is too narrow for two stat panels side by side until the viewport is very
          wide. (Deliberate arbitrary breakpoint, above the ladder's 2xl=1536, to hit
          the exact 1900px the user asked for.) Topics is Chat-only (§E.13); Call
          shape is Phone-only (§E.14). */}
      <div className="grid grid-cols-1 min-[1900px]:grid-cols-2 gap-4">
        <TimeSavedPanel
          panelKey={`${p}_time_saved`}
          say={say}
          agentValue={data?.agentValue}
          clientId={clientId}
          rangeStart={start}
          rangeEnd={end}
          modality={p}
          loading={loading}
        />
        <Workload
          panelKey={`${p}_workload`}
          say={say}
          workload={data?.agentValue?.workload}
          onDrill={onWorkloadDrill}
          loading={loading}
        />
        {/* "How guests found you", not "In guests' own words" — the Guest-questions
            panel already owns that title, and two panels with the same name are hard
            to tell apart (Phone-board Markup #9). This is the attribution/source list. */}
        <AttributionPanel
          panelKey={`${p}_attribution`}
          label="How guests found you"
          // Prefer the backend's modality-aware attribution_note for the description
          // so it can't disagree with the title (Chat Markup #24: title said "guests"
          // while the stale say-sentence still said "Where callers found you"). Falls
          // back to the existing say-map sentence when the note isn't present.
          say={data?.summary?.attribution_note
            ? { ...say, [`${p}_attribution`]: data.summary.attribution_note }
            : say}
          summary={data?.summary}
          loading={loading}
          onSelect={onAttributionSelect}
        />
        {isVoice ? (
          <CallShape panelKey="voice_call_shape" say={say} histogram={data?.callLoss} loading={loading} />
        ) : (
          <ChatQuestionsPanel
            say={say}
            topics={data?.topics}
            loading={loading}
            onTopic={onTopicSelect}
          />
        )}
      </div>

      {/* §G.4 — unknown panel_key → GenericCard. */}
      {unknownKeys.map((k) => (
        <GenericCard key={k} title={labelFor(k)} sentence={say[k]} />
      ))}
      </div>
    </div>
    </PanelLoadContext.Provider>
  );
});

export default AnalysisColumn;
